import { useTranslations } from "@fenetech/translations";
import useWait from "helpers/context/Page/useWait";
import { ThemeColorEnum } from "helpers/enums";
import { isNullOrWhiteSpace } from "helpers/objects";
import React, { useCallback, useEffect, useMemo } from 'react';
import { MaintenanceData } from "./useMaintenanceData";
import { AlertColor } from "@mui/material";
import useMessageBox from "helpers/context/Page/useMessageBox";
import useUserInfo from "helpers/context/User/useUserInfo";
import { ISaberisDocumentStatus } from "models/ISaberis";
import SaberisAPI from "components/Saberis/SaberisAPI";
import { IUploadedOrder } from "models/IUploadedOrder";

interface IProps {
    saberisDocumentStatuses: ISaberisDocumentStatus[],
    setSaberisDocumentStatuses: React.Dispatch<React.SetStateAction<ISaberisDocumentStatus[]>>,
    pagedData: MaintenanceData | undefined,
    redirectPath: string,
    setAlert: React.Dispatch<React.SetStateAction<[text: string, alertType: AlertColor] | null>>
}

const useSaberisMaintenanceHelper = ({ saberisDocumentStatuses, setSaberisDocumentStatuses, pagedData, redirectPath, setAlert }: IProps) => {

    const tm = useTranslations();
    const wait = useWait();
    const messageBox = useMessageBox();
    const user = useUserInfo();

    const saberisEnabled: boolean = useMemo(() => {
        return user.canAccessSaberis;
    }, [user]);

    const getSaberisDocumentStatuses = useCallback(async () => {
        if (pagedData) {
            try {
                wait.Show(true);
                const anyData = pagedData.data as any[];
                const oKeys: number[] = anyData.map(q => {
                    if ((q as IUploadedOrder).oKeyQuote) {
                        return (q as IUploadedOrder).oKeyQuote;
                    }
                    return q.oKey;
                });
                const result = await SaberisAPI.GetMaintenanceStatusList(oKeys);
                setSaberisDocumentStatuses(result);
            } finally {
                wait.Show(false);
            }
        }
    }, [pagedData, setSaberisDocumentStatuses, wait]);

    useEffect(() => {
        if (saberisEnabled) {
            getSaberisDocumentStatuses();
        }
    }, [saberisEnabled, getSaberisDocumentStatuses]);

    const launchSaberisLogin = useCallback(async () => {
        const urlResponse = await SaberisAPI.GetLoginURL();
        if (!urlResponse.IsOk()) {
            setAlert([tm.Get("Saberis is currently unavailable. Try again later."), "error"]);
        } else if (!isNullOrWhiteSpace(urlResponse.Result.error)) {
            setAlert([tm.GetWithParams("Saberis returned error message: '{0}'. Contact the administrator.", urlResponse.Result.error!), "error"]);
        } else {
            const baseURL = document.getElementsByTagName("base")[0].href;

            // Saberis has an interesting way of getting to its login. We need to POST to a particular URL as if we were submtitting a form.
            // The browser is expected to navigate, so a regular API call won't work.
            // Mimic the creation of the form with the correct information and submit it.
            var form = document.createElement('form');
            var callback = document.createElement('input');
            form.method = "POST";
            form.action = urlResponse.Result.loginPageURL;
            callback.value = `${baseURL}saberis/landingpage?redirectpath=${encodeURIComponent(redirectPath ?? "/dashboard")}`;
            callback.name = "CallbackURL";
            callback.style.display = "none";
            form.appendChild(callback);
            document.body.appendChild(form);
            console.log("test");
            form.submit();
        }
    }, [tm, setAlert, redirectPath]);

    const onRefreshSaberisDocument = useCallback(async (documentGuid: string) => {
        const updateDocumentStatusResponse = await SaberisAPI.QueryDocumentStatus(documentGuid);
        if (updateDocumentStatusResponse.IsOk() && updateDocumentStatusResponse.Result.authRequired) {
            messageBox.Show({ 
                message: tm.Get("Saberis authentication is required before performing this action. Proceed to the Saberis login portal?"),
                title: tm.Get("Quote Entry"),
                yesNoPrompt: true,
                callback: (dialogResult) => {
                    if (dialogResult){
                        launchSaberisLogin();
                    }
                }
            });
        } else if (!updateDocumentStatusResponse.IsOk()) {
            setAlert([tm.Get("Saberis is currently unavailable. Try again later."), "error"]);
        } else if (updateDocumentStatusResponse.Result.payload.messages?.length > 0) {
            setAlert([tm.GetWithParams("Saberis returned error message: '{0}'. Contact the administrator.", updateDocumentStatusResponse.Result.payload.messages!.join("; ")), "error"]);
        } else {
            // Need a new array reference so react picks up the change
            const currentStatuses: ISaberisDocumentStatus[] = [...saberisDocumentStatuses];
            const index = currentStatuses.findIndex(s => s.documentGuid === documentGuid);
            if (index !== -1) {
                currentStatuses[index].status = updateDocumentStatusResponse.Result.payload.status;
            }
            setSaberisDocumentStatuses(currentStatuses);
        }
    }, [launchSaberisLogin, setAlert, tm, messageBox, saberisDocumentStatuses, setSaberisDocumentStatuses]);

    const onRefreshAllSaberisDocuments = useCallback(async () => {
        if (saberisDocumentStatuses && saberisDocumentStatuses.length > 0) {
            wait.Show(true);
            try {
                const updateDocumentStatusResponse = await SaberisAPI.QueryDocumentStatuses(saberisDocumentStatuses);
                if (updateDocumentStatusResponse.IsOk() && updateDocumentStatusResponse.Result.authRequired) {
                    messageBox.Show({ 
                        message: tm.Get("Saberis authentication is required before performing this action. Proceed to the Saberis login portal?"),
                        title: tm.Get("Quote Entry"),
                        yesNoPrompt: true,
                        callback: (dialogResult) => {
                            if (dialogResult){
                                launchSaberisLogin();
                            }
                        }
                    });
                } else if (!updateDocumentStatusResponse.IsOk()) {
                    setAlert([tm.Get("Saberis is currently unavailable. Try again later."), "error"]);
                } else if (updateDocumentStatusResponse.Result.payload.length > 0) {
                    setAlert([tm.GetWithParams("Saberis returned error message: '{0}'. Contact the administrator.", updateDocumentStatusResponse.Result.payload.join("; ")), "error"]);
                } else {
                    await getSaberisDocumentStatuses();
                    setAlert([tm.Get("Saberis documents up-to-date"), ThemeColorEnum.Success]);
                }
            }
            finally {
                wait.Show(false);
            }
        }
    }, [saberisDocumentStatuses, launchSaberisLogin, getSaberisDocumentStatuses, setAlert, tm, messageBox, wait]);

    return {
        saberisEnabled,
        onRefreshSaberisDocument,
        onRefreshAllSaberisDocuments,
    }
}

export default useSaberisMaintenanceHelper;